// Vault + asset type configuration. Secret field keys must match backend SECRET_FIELD_KEYS.
export const VAULTS = {
  secrets: {
    label: "Secrets Vault",
    tagline: "Passwords, API keys, SSH keys & certificates",
    icon: "KeyRound",
    color: "#1B4332",
  },
  documents: {
    label: "Document Vault",
    tagline: "Policies, contracts, procedures & templates",
    icon: "FileText",
    color: "#7c3aed",
  },
  media: {
    label: "Media Vault",
    tagline: "Logos, images & photos",
    icon: "Image",
    color: "#0891b2",
  },
  inventory: {
    label: "Digital Asset Inventory",
    tagline: "Domains, servers, software, licenses & vendors",
    icon: "Boxes",
    color: "#16a34a",
  },
  todos: {
    label: "To-Do List",
    tagline: "Tasks, follow-ups & action items — with comments",
    icon: "ListTodo",
    color: "#ea580c",
  },
};

const F = (key, label, type = "text", extra = {}) => ({ key, label, type, ...extra });

export const TYPE_CONFIG = {
  // ---- Secrets ----
  credential: {
    vault: "secrets", label: "Credential", icon: "KeyRound",
    fields: [F("username", "Username / Login"), F("password", "Password", "password"),
      F("url", "URL"), F("mfa", "MFA Enabled", "select", { options: ["Yes", "No"] })],
  },
  api_key: {
    vault: "secrets", label: "API Key", icon: "Code2",
    fields: [F("service", "Service"), F("api_key", "API Key", "password"), F("environment", "Environment")],
  },
  ssh_key: {
    vault: "secrets", label: "SSH Key", icon: "Terminal",
    fields: [F("host", "Host"), F("username", "Username"), F("private_key", "Private Key", "password"), F("passphrase", "Passphrase", "password")],
  },
  certificate: {
    vault: "secrets", label: "Certificate", icon: "ShieldCheck",
    fields: [F("issuer", "Issuer"), F("domain", "Domain"), F("secret", "Private Key / Secret", "password")],
  },
  recovery: {
    vault: "secrets", label: "Recovery Codes", icon: "LifeBuoy",
    fields: [F("service", "Service"), F("recovery_codes", "Recovery Codes", "password")],
  },
  // ---- Documents ----
  policy: { vault: "documents", label: "Policy", icon: "Gavel",
    fields: [F("version", "Version"), F("approved_on", "Approved On", "date"), F("owner_dept", "Owner Dept")], file: true },
  contract: { vault: "documents", label: "Contract", icon: "FileSignature",
    fields: [F("counterparty", "Counterparty"), F("value", "Value"), F("signed_on", "Signed On", "date")], file: true },
  procedure: { vault: "documents", label: "Procedure", icon: "ListChecks",
    fields: [F("version", "Version"), F("system", "Related System")], file: true },
  template: { vault: "documents", label: "Template", icon: "LayoutTemplate",
    fields: [F("format", "Format"), F("use_case", "Use Case")], file: true },
  document: { vault: "documents", label: "Document", icon: "FileText",
    fields: [F("doc_type", "Document Type")], file: true },
  // ---- Media ----
  logo: { vault: "media", label: "Logo", icon: "Sparkles",
    fields: [F("brand", "Brand"), F("format", "Format (SVG/PNG)"), F("approved", "Approved", "select", { options: ["Yes", "No"] })], file: true },
  image: { vault: "media", label: "Image", icon: "Image",
    fields: [F("subject", "Subject")], file: true },
  photo: { vault: "media", label: "Photo", icon: "Camera",
    fields: [F("location", "Location"), F("taken_on", "Taken On", "date")], file: true },
  // ---- Inventory ----
  domain: { vault: "inventory", label: "Domain", icon: "Globe",
    fields: [F("registrar", "Registrar"), F("dns_provider", "DNS Provider"), F("auto_renew", "Auto-renew", "select", { options: ["Yes", "No"] })] },
  server: { vault: "inventory", label: "Server", icon: "Server",
    fields: [F("ip", "IP Address"), F("provider", "Hosting Provider"), F("os", "Operating System"), F("environment", "Environment")] },
  software: { vault: "inventory", label: "Software", icon: "AppWindow",
    fields: [F("vendor", "Vendor"), F("version", "Version"), F("seats", "Seats")] },
  license: { vault: "inventory", label: "License", icon: "BadgeCheck",
    fields: [F("license_key_note", "License Ref"), F("seats", "Seats"), F("vendor", "Vendor")] },
  vendor: { vault: "inventory", label: "Vendor", icon: "Building2",
    fields: [F("contact_name", "Contact"), F("contact_email", "Email"), F("phone", "Phone"), F("website", "Website")] },
  subscription: { vault: "inventory", label: "Subscription", icon: "CreditCard",
    fields: [F("provider", "Provider"), F("plan", "Plan"), F("billing", "Billing Cycle")] },
  device: { vault: "inventory", label: "Device", icon: "Laptop",
    fields: [F("serial", "Serial No."), F("assigned_to", "Assigned To"), F("model", "Model")] },
  account: { vault: "inventory", label: "Account", icon: "UserCircle",
    fields: [F("platform", "Platform"), F("account_id", "Account ID")] },
  // ---- To-Do ----
  todo: { vault: "todos", label: "To-Do", icon: "ListTodo", file: true,
    fields: [F("priority", "Priority", "select", { options: ["Low", "Medium", "High"] }),
      F("due_date", "Due date", "date"),
      F("assignee", "Assignee")] },
};

export const TODO_STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "on_hold", label: "On hold" },
  { value: "completed", label: "Completed" },
];

export const statusLabel = (s) => {
  const t = TODO_STATUSES.find((x) => x.value === s);
  if (t) return t.label;
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

export const SECRET_KEYS = ["password", "api_key", "secret", "private_key", "recovery_codes", "token", "passphrase"];

export function typesForVault(vault) {
  return Object.entries(TYPE_CONFIG).filter(([, c]) => c.vault === vault).map(([k, c]) => ({ key: k, ...c }));
}
